import { useQuery, useMutation, useQueryClient } from 'react-query';
import { GetSeatById, AddNewSeat, UpdateSeat, DeleteSeat, GetSeats } from '../services/backend';
import { Notify, NotifyError } from '../utils/functions';

export function useGetSeats(spaceId = 'all', filter = {}) {
    return useQuery(['seats', spaceId, filter], () => GetSeats(spaceId, filter), {
        refetchOnWindowFocus: false,
        staleTime: 5 * 60 * 1000, // 5 minutes
        select: (data) => {
            return data?.data?.seats || [];
        },
        onError: (error) => {
            NotifyError("Failed to fetch seats", error);
            console.error("Error fetching seats:", error);
        }
    });
}

export function useGetSeatById(seatId) {
    return useQuery(
        ['seat', seatId],
        () => GetSeatById(seatId),
        {
            enabled: !!seatId,
            staleTime: 5 * 60 * 1000, // 5 minutes
            onError: (error) => {
                NotifyError("Failed to fetch seat details", error);
                console.error("Error fetching seat details:", error);
            }
        }
    );
}

export function useAddSeat() {
    const queryClient = useQueryClient();
    return useMutation(({ spaceId, data }) => AddNewSeat(spaceId, data), {
        onSuccess: () => {
            Notify("Seat added successfully");
            queryClient.invalidateQueries('seats');
        },
        onError: (error) => {
            NotifyError("Failed to add seat", error);
            console.error("Error adding seat:", error);
        }
    });
}

export function useUpdateSeat() {
    const queryClient = useQueryClient();
    return useMutation(({ seatId, data }) => UpdateSeat(seatId, data), {
        onSuccess: () => {
            Notify("Seat updated successfully");
            queryClient.invalidateQueries('seats');
        },
        onError: (error) => {
            NotifyError("Failed to update seat", error);
            console.error("Error updating seat:", error);
        }
    });
}

export function useDeleteSeat() {
    const queryClient = useQueryClient();
    return useMutation(({ seatId }) => DeleteSeat(seatId), {
        onSuccess: () => {
            Notify("Seat deleted successfully");
            queryClient.invalidateQueries('seats');
        },
        onError: (error) => {
            NotifyError("Failed to delete seat", error);
            console.error("Error deleting seat:", error);
        }
    });
}

export function useUpdateSeatStatus() {
    const queryClient = useQueryClient();
    return useMutation(({ seatId, status }) => UpdateSeat(seatId, { status }), {
        onSuccess: () => {
            Notify("Seat status updated successfully");
            queryClient.invalidateQueries('seat');
        },
        onError: (error) => {
            NotifyError("Failed to update seat status", error);
            console.error("Error updating seat status:", error);
        }
    });
}

export function useUpdateSeatMaintenance() {
    const queryClient = useQueryClient();
    return useMutation(({ seatId, underMaintenance }) => UpdateSeat(seatId, { underMaintenance }), {
        onSuccess: () => {
            Notify("Seat maintenance updated successfully");
            queryClient.invalidateQueries('seat');
        },
        onError: (error) => {
            NotifyError("Failed to update seat maintenance", error);
            console.error("Error updating seat maintenance:", error);
        }
    });
}

export function useUpdateSeatRates() {
    const queryClient = useQueryClient();
    return useMutation(({ seatId, ratePerHour, ratePerDay, ratePerMonth }) => UpdateSeat(seatId, { ratePerHour, ratePerDay, ratePerMonth }), {
        onSuccess: () => {
            Notify("Seat rates updated successfully");
            queryClient.invalidateQueries('seat');
        },
        onError: (error) => {
            NotifyError("Failed to update seat rates", error);
            console.error("Error updating seat rates:", error);
        }
    });
}